// src/components/QALayout/Layout2.js
import React, { useEffect, useState } from 'react';

const Layout2 = ({
  questions = [],
  getQuestionPath,
  selectedQuestionId,
  onSelectQuestion,
  onQuestionClick,
  onQuestionKeyDown,
}) => {
  const activeQuestion =
    questions.find((q) => (q._id || q.id) === selectedQuestionId) || questions[0] || null;

  const getIsBelowLayoutBreakpoint = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia('(max-width: 75em)').matches;
  };

  const [isBelowLayoutBreakpoint, setIsBelowLayoutBreakpoint] = useState(
    getIsBelowLayoutBreakpoint()
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 75em)');

    const handleChange = (event) => {
      setIsBelowLayoutBreakpoint(event.matches);
    };

    setIsBelowLayoutBreakpoint(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const handleSelectOrNavigate = (questionId, path) => {
    if (isBelowLayoutBreakpoint) {
      if (path && onQuestionClick) {
        onQuestionClick(path);
      }
      return;
    }

    if (onSelectQuestion) {
      onSelectQuestion(questionId);
    }
  };

  const handleSelectOrNavigateKeyDown = (ev, questionId, path) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') {
      return;
    }

    ev.preventDefault();

    if (isBelowLayoutBreakpoint) {
      if (path && onQuestionKeyDown) {
        onQuestionKeyDown(ev, path);
      } else if (path && onQuestionClick) {
        onQuestionClick(path);
      }
      return;
    }

    if (onSelectQuestion) {
      onSelectQuestion(questionId);
    }
  };

  return (
    <section
      className="container-fluid qa-feature-layout-shell"
      aria-label="Questions and Answers"
    >
      <div className="qa-feature-layout">
        <div className="qa-feature-layout__questions-column">
          <div className="qa-feature-layout__questions-scroll">
            {questions.length > 0 ? (
              questions.map((q) => {
                const questionId = q._id || q.id;
                const path = getQuestionPath ? getQuestionPath(q) : '';
                const isActive =
                  activeQuestion && (activeQuestion._id || activeQuestion.id) === questionId;

                const showActiveIcon = !isBelowLayoutBreakpoint && isActive;

                return (
                  <article
                    key={questionId}
                    className={`qa-feature-layout__question-row ${isActive ? 'is-active' : ''}`}
                  >
                    <div className="qa-feature-layout__question-main">
                      <button
                        type="button"
                        className="qa-feature-layout__question-select"
                        onClick={() => handleSelectOrNavigate(questionId, path)}
                        onKeyDown={(ev) =>
                          handleSelectOrNavigateKeyDown(ev, questionId, path)
                        }
                        aria-pressed={isActive}
                        aria-label={`Select question: ${q.question}`}
                      >
                        <h5 className="qa-feature-layout__question-text">{q.question}</h5>
                      </button>
                    </div>

                    <div className="qa-feature-layout__question-actions">
                      <button
                        type="button"
                        className={`qa-feature-layout__question-indicator ${
                          showActiveIcon ? 'is-active' : ''
                        }`}
                        onClick={() => handleSelectOrNavigate(questionId, path)}
                        onKeyDown={(ev) =>
                          handleSelectOrNavigateKeyDown(ev, questionId, path)
                        }
                        aria-label={`Preview answer for: ${q.question}`}
                      >
                        {showActiveIcon ? (
                          <svg
                            className="qa-feature-layout__question-indicator-icon qa-feature-layout__question-indicator-icon--active"
                            width="8"
                            height="6"
                            viewBox="0 0 8 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M5.33333 5.33333L4.625 4.625L6.08333 3.16667H0V2.16667H6.08333L4.625 0.708334L5.33333 0L8 2.66667L5.33333 5.33333Z"
                              fill="currentColor"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="qa-feature-layout__question-indicator-icon qa-feature-layout__question-indicator-icon--default"
                            width="14"
                            height="15"
                            viewBox="0 0 14 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M0.416748 14.0104V10.4271H1.50008V12.1531L4.14591 9.50729L4.91987 10.2812L2.27404 12.9271H4.00008V14.0104H0.416748ZM9.85425 5.34688L9.08029 4.57292L11.7261 1.92708H10.0001V0.84375H13.5834V4.42708H12.5001V2.70104L9.85425 5.34688Z"
                              fill="currentColor"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="qa-feature-layout__empty">No questions yet.</div>
            )}
          </div>
        </div>

        <div className="qa-feature-layout__answer-column">
          {activeQuestion ? (
            <article className="qa-feature-layout__answer-card">
              <div className="qa-feature-layout__answer-top">
                <div className="badge-question small">
                  <div className="badge-question-number small">Q</div>
                </div>
              </div>

              <header className="qa-feature-layout__answer-header">
                <h3 className="qa-feature-layout__answer-title">{activeQuestion.question}</h3>
              </header>

              <div className="qa-feature-layout__answer-content">
                {activeQuestion.detail && (
                  <div className="qa-feature-layout__answer-detail">
                    <p>{activeQuestion.detail}</p>
                  </div>
                )}

                <div className="divider dotted" />

                <div className="qa-feature-layout__answer-top">
                  <div className="badge-question small">
                    <div className="badge-question-number small">A</div>
                  </div>
                </div>

                {activeQuestion.answer && (
                  <div className="qa-feature-layout__answer-text">
                    <p>{activeQuestion.answer}</p>
                  </div>
                )}

                {activeQuestion.source && (
                  <div className="source-label">
                    <svg
                      className="source-label-icon"
                      width="21"
                      height="20"
                      viewBox="0 0 21 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <g opacity="0.5">
                        <path
                          d="M10.2497 5.82422C10.0136 5.82422 9.81565 5.90408 9.65593 6.0638C9.4962 6.22352 9.41634 6.42144 9.41634 6.65755C9.41634 6.89366 9.4962 7.09158 9.65593 7.2513C9.81565 7.41102 10.0136 7.49088 10.2497 7.49088C10.4858 7.49088 10.6837 7.41102 10.8434 7.2513C11.0031 7.09158 11.083 6.89366 11.083 6.65755C11.083 6.42144 11.0031 6.22352 10.8434 6.0638C10.6837 5.90408 10.4858 5.82422 10.2497 5.82422ZM11.083 9.15755L9.41634 9.15755L9.41634 14.1576L11.083 14.1576L11.083 9.15755ZM10.2497 1.65755C11.4025 1.65755 12.4858 1.8763 13.4997 2.3138C14.5136 2.7513 15.3955 3.34505 16.1455 4.09505C16.8955 4.84505 17.4893 5.727 17.9268 6.74088C18.3643 7.75477 18.583 8.83811 18.583 9.99089C18.583 11.1437 18.3643 12.227 17.9268 13.2409C17.4893 14.2548 16.8955 15.1367 16.1455 15.8867C15.3955 16.6367 14.5136 17.2305 13.4997 17.668C12.4858 18.1055 11.4025 18.3242 10.2497 18.3242C9.0969 18.3242 8.01356 18.1055 6.99968 17.668C5.98579 17.2305 5.10384 16.6367 4.35384 15.8867C3.60384 15.1367 3.01009 14.2548 2.57259 13.2409C2.13509 12.227 1.91634 11.1437 1.91634 9.99088C1.91634 8.83811 2.1351 7.75477 2.5726 6.74088C3.0101 5.72699 3.60384 4.84505 4.35384 4.09505C5.10384 3.34505 5.98579 2.7513 6.99968 2.3138C8.01357 1.8763 9.0969 1.65755 10.2497 1.65755ZM10.2497 3.32422C8.38857 3.32422 6.81218 3.97005 5.52051 5.26172C4.22884 6.55338 3.58301 8.12977 3.58301 9.99088C3.58301 11.852 4.22884 13.4284 5.52051 14.7201C6.81218 16.0117 8.38857 16.6576 10.2497 16.6576C12.1108 16.6576 13.6872 16.0117 14.9788 14.7201C16.2705 13.4284 16.9163 11.852 16.9163 9.99088C16.9163 8.12977 16.2705 6.55338 14.9788 5.26172C13.6872 3.97005 12.1108 3.32422 10.2497 3.32422Z"
                          fill="currentColor"
                        />
                      </g>
                    </svg>
                    <p>{activeQuestion.source}</p>
                  </div>
                )}
              </div>
            </article>
          ) : (
            <div className="qa-feature-layout__empty-panel">
              <div className="qa-feature-layout__empty">No questions yet.</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Layout2;
